import { Building2Icon, UserIcon } from "lucide-react";
import { Link } from "react-router";

import type { IChatConversationListItem } from "@/api/chat/chat.types";
import { TeamVisibleBadge } from "@/components/shared/TeamVisibleBadge";
import {
    Item,
    ItemActions,
    ItemContent,
    ItemTitle,
} from "@/components/ui/item";
import { activityTime, getConversationTitle } from "@/utils/chatConversations";

import { CHAT_PATH } from "../chatRoutes";
import { absoluteTime, rowTimestamp } from "./chatHistoryDates";
import { HighlightedText } from "./HighlightedText";

interface ChatHistoryRowProps {
    row: IChatConversationListItem;
    terms: string[];
    now: Date;
}

export function ChatHistoryRow({ row, terms, now }: ChatHistoryRowProps) {
    const iso = activityTime(row);
    const clientName = row.client?.name;
    const isShared = row.access_mode === "collaborator";

    return (
        <Item size="sm" render={<Link to={`${CHAT_PATH}/${row.id}`} />}>
            <ItemContent className="min-w-0">
                <ItemTitle className="max-w-full">
                    <HighlightedText
                        text={getConversationTitle(row)}
                        terms={terms}
                        className="truncate"
                    />
                </ItemTitle>
                {clientName || isShared ? (
                    <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs leading-4">
                        {clientName ? (
                            <div className="flex min-w-0 items-center gap-1.5">
                                <Building2Icon
                                    aria-hidden
                                    className="size-3 shrink-0"
                                />
                                <HighlightedText
                                    text={clientName}
                                    terms={terms}
                                    className="truncate"
                                />
                            </div>
                        ) : null}
                        {isShared && row.is_team_visible ? (
                            <TeamVisibleBadge />
                        ) : null}
                        {isShared && !row.is_team_visible ? (
                            <div className="flex min-w-0 items-center gap-1.5">
                                <UserIcon
                                    aria-hidden
                                    className="size-3 shrink-0"
                                />
                                <span className="truncate">
                                    {row.shared_by_display_name ? (
                                        <>
                                            Shared by{" "}
                                            <HighlightedText
                                                text={
                                                    row.shared_by_display_name
                                                }
                                                terms={terms}
                                            />
                                        </>
                                    ) : (
                                        "Shared"
                                    )}
                                </span>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </ItemContent>
            <ItemActions>
                <time
                    dateTime={iso}
                    title={absoluteTime(iso)}
                    className="text-muted-foreground text-xs leading-4 whitespace-nowrap"
                >
                    {rowTimestamp(iso, now)}
                </time>
            </ItemActions>
        </Item>
    );
}
